import React, { SyntheticEvent } from 'react'
import Spin from 'antd/lib/spin'
import Input from 'antd/lib/input'
import message from 'antd/lib/message'
import isEmpty from 'lodash/isEmpty'
import { HiMiniMagnifyingGlass } from 'react-icons/hi2'
import PlacesAutocomplete, {
  geocodeByPlaceId,
  getLatLng
} from 'react-places-autocomplete'
import { extractGoogleAddressComponents } from '@slerp/helpers'
import styled from '@emotion/styled'

export type AddressValueType = {
  geocoded?: boolean
  validateTrigger?: boolean
  postalCode?: string
  formattedAddress?: string
  noSuggestionSelected?: boolean
  line1?: string
  line2?: string
  city?: string
  country?: string
  lat?: number
  lng?: number
}

interface Props {
  value?: string
  placeholder?: string
  onChange?: (value: string, geocodedAddress: AddressValueType) => void
  onInput?: (e: SyntheticEvent<HTMLInputElement>) => void
  disabled?: boolean
}

const AutocompleteInput = ({
  value,
  onChange,
  onInput,
  disabled = false,
  placeholder
}: Props) => {
  // Render a basic input without autocomplete if Google Maps is not loaded
  if (typeof google === 'undefined' || !google.maps) {
    return (
      <StyledContainer>
        <StyledInputPassword
          placeholder={placeholder}
          className='location-search-input'
          disabled={disabled}
          onInput={onInput}
          prefix={<HiMiniMagnifyingGlass />}
          data-testid='address-input-field'
        />
      </StyledContainer>
    )
  }

  const LONDON = new google.maps.LatLng(51.5285582, -0.2416806)
  const options = {
    location: LONDON,
    radius: 23.5,
    types: ['geocode', 'establishment'],
    componentRestrictions: { country: 'GB' }
  }

  const handleOnChange = (address: string, placeId?: string) => {
    if (!placeId) {
      return (
        onChange &&
        onChange(address, {
          geocoded: false,
          validateTrigger: false,
          noSuggestionSelected: true
        })
      )
    }

    geocodeByPlaceId(placeId)
      .then(async (res) => ({
        result: res[0],
        latLng: await getLatLng(res[0])
      }))
      .then(({ result, latLng }) => {
        const { formatted_address, address_components } = result
        const components = extractGoogleAddressComponents(address_components)
        const { line1, line2, streetNumber, city, zip } = components
        const addressHash = {
          geocoded: true,
          validateTrigger: true,
          noSuggestionSelected: false,
          formattedAddress: formatted_address,
          line1: streetNumber ? `${streetNumber} ${line2}` : line2,
          line2: line1,
          city: city,
          country: 'GB',
          postalCode: zip,
          lat: latLng.lat,
          lng: latLng.lng
        }

        onChange && onChange(address, addressHash)
      })
  }

  return (
    <PlacesAutocomplete
      value={value}
      onChange={handleOnChange}
      onSelect={handleOnChange}
      searchOptions={options}
      onError={(status) => message.warn(`Invalid address: ${status}`)}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <StyledContainer>
          <StyledInputPassword
            {...getInputProps({
              placeholder,
              className: 'location-search-input',
              disabled: disabled
            })}
            onInput={onInput}
            prefix={<HiMiniMagnifyingGlass />}
            data-testid='address-input-field'
          />
          <StyledDiv show={!isEmpty(suggestions)}>
            {loading && <Spin />}
            {suggestions.map((suggestion) => {
              const className = suggestion.active
                ? 'suggestion-item--active'
                : 'suggestion-item'
              const style = suggestion.active
                ? {
                    backgroundColor: '#fafafa',
                    cursor: 'pointer',
                    padding: '5px 10px'
                  }
                : {
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    padding: '5px 10px'
                  }
              return (
                <div
                  {...getSuggestionItemProps(suggestion, {
                    className,
                    style
                  })}
                >
                  <span>{suggestion.description}</span>
                </div>
              )
            })}
          </StyledDiv>
        </StyledContainer>
      )}
    </PlacesAutocomplete>
  )
}

const StyledInputPassword = styled(Input)`
  &.ant-input-affix-wrapper-focused .ant-input {
    border: none;
  }
`

const StyledContainer = styled.div`
  position: relative;
`

const StyledDiv = styled.div((props: { show: boolean }) => ({
  marginTop: '5px',
  position: 'absolute',
  left: 0,
  top: '100%',
  zIndex: 10,
  width: '100%',
  backgroundColor: '#fff',
  border: props.show ? '1px solid #44446e' : 'none',
  borderRadius: '5px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden'
}))

export default AutocompleteInput
